import { Children, type ReactNode } from 'react';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import Box, { type BoxProps } from '@mui/material/Box';
import { duration, ease } from './tokens';

/** MUI Box with motion props (initial, animate, variants...). */
export const MotionBox = motion.create(Box);

/**
 * Motion primitives. Entrances rise and sharpen into place with an expo-out curve, text arrives
 * word by word. Everything collapses to a plain fade under prefers-reduced-motion.
 */

const riseVariants = (distance: number): Variants => ({
    hidden: { opacity: 0, y: distance, filter: 'blur(6px)' },
    show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: duration.reveal, ease: ease.out } },
});

const fadeVariants: Variants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: duration.base } },
};

interface RevealProps {
    children: ReactNode;
    /** Seconds before the entrance starts. */
    delay?: number;
    /** Pixels to rise from. */
    distance?: number;
    /** Animate when scrolled into view instead of on mount. */
    inView?: boolean;
    sx?: BoxProps['sx'];
    component?: 'div' | 'section' | 'li' | 'span';
}

/** Fade, rise and un-blur into place. */
export function Reveal({ children, delay = 0, distance = 24, inView = false, sx, component = 'div' }: RevealProps) {
    const reduce = useReducedMotion();
    const variants = reduce ? fadeVariants : riseVariants(distance);
    const trigger = inView
        ? { whileInView: 'show', viewport: { once: true, margin: '-10% 0px' } }
        : { animate: 'show' };
    return (
        <MotionBox sx={sx} component={component} initial="hidden" {...trigger} variants={variants} transition={{ delay }}>
            {children}
        </MotionBox>
    );
}

interface StaggerProps {
    children: ReactNode;
    /** Seconds between children. */
    gap?: number;
    delay?: number;
    sx?: BoxProps['sx'];
    inView?: boolean;
}

/** Reveal children one after another. Wrap each child in <StaggerItem>. */
export function Stagger({ children, gap = 0.07, delay = 0, sx, inView = false }: StaggerProps) {
    const trigger = inView
        ? { whileInView: 'show', viewport: { once: true, margin: '-10% 0px' } }
        : { animate: 'show' };
    return (
        <MotionBox
            sx={sx}
            initial="hidden"
            {...trigger}
            variants={{ hidden: {}, show: { transition: { staggerChildren: gap, delayChildren: delay } } }}
        >
            {children}
        </MotionBox>
    );
}

export function StaggerItem({ children, sx, distance = 18 }: { children: ReactNode; sx?: BoxProps['sx']; distance?: number }) {
    const reduce = useReducedMotion();
    return (
        <MotionBox sx={sx} variants={reduce ? fadeVariants : riseVariants(distance)}>
            {children}
        </MotionBox>
    );
}

interface SplitWordsProps {
    text: string;
    delay?: number;
    /** Seconds between words. */
    gap?: number;
    /** Words to highlight with the spark accent gradient (exact match, case-insensitive). */
    highlight?: string[];
}

/**
 * Headline that arrives word by word, each word sliding up out of a mask.
 * Render inside a Typography so it inherits the type style.
 */
export function SplitWords({ text, delay = 0, gap = 0.06, highlight = [] }: SplitWordsProps) {
    const reduce = useReducedMotion();
    const words = text.split(/\s+/).filter(Boolean);
    const lit = new Set(highlight.map(w => w.toLowerCase()));
    return (
        <Box component="span" sx={{ display: 'inline' }} aria-label={text}>
            {words.map((word, i) => (
                <Box
                    key={`${word}-${i}`}
                    component="span"
                    aria-hidden
                    sx={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'top', pb: '0.08em', mr: '0.25em' }}
                >
                    <MotionBox
                        component="span"
                        sx={{
                            display: 'inline-block',
                            ...(lit.has(word.replace(/[^\p{L}\p{N}-]/gu, '').toLowerCase())
                                ? {
                                    background: 'linear-gradient(100deg, #ffd27a 0%, #ffb12e 40%, #2fb0ff 100%)',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                    color: 'transparent',
                                }
                                : {}),
                        }}
                        initial={reduce ? { opacity: 0 } : { y: '110%' }}
                        animate={reduce ? { opacity: 1 } : { y: '0%' }}
                        transition={{ duration: duration.reveal, ease: ease.out, delay: delay + i * gap }}
                    >
                        {word}
                    </MotionBox>
                </Box>
            ))}
        </Box>
    );
}

/** Page-level transition used by the shell around each route. */
export function PageFade({ children, routeKey }: { children: ReactNode; routeKey: string }) {
    const reduce = useReducedMotion();
    // Entrance only, and short: the old page leaves instantly so navigation never waits on an animation
    return (
        <MotionBox
            key={routeKey}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: duration.page, ease: ease.out }}
            sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
            {Children.only(children)}
        </MotionBox>
    );
}
